import { API_BASE_URL } from './config';

/* ----- Result types (API 응답을 뷰에 맞게 매핑한 타입) ----- */

export interface SmartMoneyWallet {
  wallet: string;
  twitterPfp: string;
  twitterUsername: string;
  twitterFollowers: number;
  performance: string;
  tokenAmount: number;
  avgEntryMC: string;
  avgHoldingDuration: string;
  pnl?: number | null;
  winRate?: number | null;
  scalpingCount?: number | null;
}

export interface OtherTokenActivity {
  tokenAddress?: string;
  tokenSymbol?: string;
  role?: string;
  maxMc?: number;
}

export interface AdminModProfile {
  name: string;
  screenName: string;
  role: 'admin' | 'moderator';
  followersCount: number;
  otherTokenActivities: OtherTokenActivity[];
}

export interface FrequentlyQuotedTweet {
  text: string;
  url?: string;
  id?: string;
  likeCount?: number;
  retweetCount?: number;
  replyCount?: number;
  viewCount?: number | null;
  authorName?: string;
  authorHandle?: string;
  authorPfp?: string;
  authorFollowers?: number;
  createdAt?: string;
}

/** SocialSection용 (현재 API에는 미포함, 추후 확장 시 사용) */
export interface Tweet {
  author: string;
  handle: string;
  content: string;
  stats: string;
}

/** Token 정보 (API token 객체와 동일한 구조) */
export interface TokenInfo {
  address?: string;
  symbol?: string;
  totalSupply?: string;
  decimals?: number;
  website?: string;
  imageUrl?: string | null;
  createdOn?: string;
  tokenSource?: string;
  poolAddress?: string;
  poolCreatedAt?: string;
}

export interface TokenAnalysisResult {
  /** @deprecated result.token.symbol 사용 */
  symbol?: string;
  /** @deprecated result.token.imageUrl 사용 */
  imageUrl?: string | null;
  token?: TokenInfo;
  content?: { text: string };
  smartMoney?: {
    text: string;
    table: SmartMoneyWallet[];
  };
  adminMod?: {
    text: string;
    table: AdminModProfile[];
  };
  frequentlyQuoted?: {
    tweets: FrequentlyQuotedTweet[];
  };
  /** 3종 트윗: mostViewed, highFollower, kol */
  tweetSections?: {
    mostViewed: FrequentlyQuotedTweet[];
    highFollower: FrequentlyQuotedTweet[];
    kol: FrequentlyQuotedTweet[];
  };
}

/* ----- Aggregate Info API (GET /v1/{tokenAddress}/aggregate-info) ----- */

export interface AggregateInfoSmartMoneyRow {
  wallet: string;
  twitterPfp?: string | null;
  twitterUsername?: string | null;
  twitterFollowers?: number;
  performance?: string;
  tokenAmount: number;
  avgEntryMC?: number | null;
  avgHoldingDurationSec?: number | null;
  pnl?: number | null;
  winRate?: number | null;
  scalpingCount?: number | null;
  scalpingRatio?: number | null;
}

export interface AggregateInfoAdminModRow {
  name: string;
  screenName: string;
  role: 'admin' | 'moderator' | string;
  followersCount: number;
  otherTokenActivities: OtherTokenActivity[];
}

export interface AggregateInfoTweetAuthor {
  name?: string;
  userName?: string;
  profilePicture?: string;
  followers?: number;
  [key: string]: unknown;
}

export interface AggregateInfoTweetRow {
  _id?: string;
  id?: string;
  url?: string;
  text?: string;
  likeCount?: number;
  retweetCount?: number;
  replyCount?: number;
  viewCount?: number | null;
  author?: AggregateInfoTweetAuthor;
  createdAt?: string;
  created_at?: string;
  [key: string]: unknown;
}

export interface AggregateInfoToken {
  address?: string;
  symbol?: string;
  totalSupply?: string;
  decimals?: number;
  website?: string;
  imageUrl?: string | null;
  createdOn?: string;
  tokenSource?: string;
  poolAddress?: string;
  poolCreatedAt?: string;
}

export interface AggregateInfoResponse {
  token?: AggregateInfoToken;
  tokenSymbol?: string;
  imageUrl?: string | null;
  content?: { text: string };
  smartMoney?: {
    text: string;
    table: AggregateInfoSmartMoneyRow[];
  };
  adminMod?: {
    text: string;
    table: AggregateInfoAdminModRow[];
  };
  frequentlyQuoted?: { tweets: AggregateInfoTweetRow[] };
  tweetSections?: {
    mostViewed: AggregateInfoTweetRow[];
    highFollower: AggregateInfoTweetRow[];
    kol: AggregateInfoTweetRow[];
  };
}

function formatMc(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${Math.round(value)}`;
}

function formatDuration(sec: number | null | undefined): string {
  if (sec == null || Number.isNaN(sec)) return '—';
  if (sec >= 86400) return `${Math.round(sec / 86400)}d`;
  if (sec >= 3600) return `${Math.round(sec / 3600)}h`;
  if (sec >= 60) return `${Math.round(sec / 60)}m`;
  return `${sec}s`;
}

function mapAggregateToResult(raw: AggregateInfoResponse): TokenAnalysisResult {
  const result: TokenAnalysisResult = {};
  result.symbol = raw.token?.symbol ?? raw.tokenSymbol ?? undefined;
  result.imageUrl = raw.token?.imageUrl ?? raw.imageUrl ?? undefined;
  if (raw.token) {
    result.token = {
      address: raw.token.address,
      symbol: raw.token.symbol,
      totalSupply: raw.token.totalSupply,
      decimals: raw.token.decimals,
      website: raw.token.website,
      imageUrl: raw.token.imageUrl,
      createdOn: raw.token.createdOn,
      tokenSource: raw.token.tokenSource,
      poolAddress: raw.token.poolAddress,
      poolCreatedAt: raw.token.poolCreatedAt,
    };
  }

  if (raw.content?.text) {
    result.content = { text: raw.content.text };
  }

  if (raw.smartMoney) {
    const table: SmartMoneyWallet[] = raw.smartMoney.table.map((row) => ({
      wallet: row.wallet,
      twitterPfp: row.twitterPfp ?? '',
      twitterUsername: row.twitterUsername ? (row.twitterUsername.startsWith('@') ? row.twitterUsername : `@${row.twitterUsername}`) : '—',
      twitterFollowers: row.twitterFollowers ?? 0,
      performance: row.performance ?? '—',
      tokenAmount: row.tokenAmount,
      avgEntryMC: formatMc(row.avgEntryMC),
      avgHoldingDuration: formatDuration(row.avgHoldingDurationSec),
      pnl: row.pnl ?? undefined,
      winRate: row.winRate ?? undefined,
      scalpingCount: row.scalpingCount ?? row.scalpingRatio ?? undefined,
    }));
    result.smartMoney = { text: raw.smartMoney.text, table };
  }

  if (raw.adminMod) {
    const table: AdminModProfile[] = raw.adminMod.table.map((row) => ({
      name: row.name,
      screenName: row.screenName,
      role: (row.role === 'admin' ? 'admin' : 'moderator') as AdminModProfile['role'],
      followersCount: row.followersCount,
      otherTokenActivities: Array.isArray(row.otherTokenActivities) ? row.otherTokenActivities : [],
    }));
    result.adminMod = { text: raw.adminMod.text, table };
  }

  if (raw.frequentlyQuoted?.tweets?.length) {
    const author = (t: AggregateInfoTweetRow) => t.author;
    result.frequentlyQuoted = {
      tweets: raw.frequentlyQuoted.tweets
        .filter((t): t is AggregateInfoTweetRow => t != null && (t.text != null || t.url != null))
        .map((t) => ({
          text: t.text ?? '',
          url: t.url,
          id: String(t.id ?? t._id ?? ''),
          likeCount: t.likeCount,
          retweetCount: t.retweetCount,
          replyCount: t.replyCount,
          authorName: author(t)?.name,
          authorHandle: author(t)?.userName,
          authorPfp: author(t)?.profilePicture,
          authorFollowers: author(t)?.followers,
          createdAt: t.createdAt ?? t.created_at,
        })),
    };
  }

  const mapTweetRow = (t: AggregateInfoTweetRow): FrequentlyQuotedTweet => {
    const a = t.author;
    return {
      text: t.text ?? '',
      url: t.url,
      id: String(t.id ?? t._id ?? ''),
      likeCount: t.likeCount,
      retweetCount: t.retweetCount,
      replyCount: t.replyCount,
      viewCount: t.viewCount ?? undefined,
      authorName: a?.name,
      authorHandle: a?.userName,
      authorPfp: a?.profilePicture,
      authorFollowers: a?.followers,
      createdAt: typeof t.createdAt === 'string' ? t.createdAt : (t.created_at as string),
    };
  };

  if (raw.tweetSections) {
    result.tweetSections = {
      mostViewed: (raw.tweetSections.mostViewed ?? [])
        .filter((t): t is AggregateInfoTweetRow => t != null && (t.text != null || t.url != null))
        .map(mapTweetRow),
      highFollower: (raw.tweetSections.highFollower ?? [])
        .filter((t): t is AggregateInfoTweetRow => t != null && (t.text != null || t.url != null))
        .map(mapTweetRow),
      kol: (raw.tweetSections.kol ?? [])
        .filter((t): t is AggregateInfoTweetRow => t != null && (t.text != null || t.url != null))
        .map(mapTweetRow),
    };
  }

  return result;
}

export async function fetchAggregateInfo(
  tokenAddress: string,
  signal?: AbortSignal
): Promise<AggregateInfoResponse | { data: AggregateInfoResponse; meta?: unknown }> {
  const url = `${API_BASE_URL}/v1/${encodeURIComponent(tokenAddress)}/aggregate-info`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Aggregate info failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/** API가 { data: {...} } 형태로 올 수 있음 */
function unwrapData(res: AggregateInfoResponse | { data: AggregateInfoResponse }): AggregateInfoResponse {
  if (res != null && 'data' in res && res.data != null) return res.data as AggregateInfoResponse;
  return res as AggregateInfoResponse;
}

/** 검색 결과용: fetchAggregateInfo 호출 후 TokenAnalysisResult로 매핑해 반환. signal 전달 시 해당 요청 취소 가능. */
export async function fetchTokenAnalysis(
  tokenAddress: string,
  signal?: AbortSignal
): Promise<TokenAnalysisResult> {
  const res = await fetchAggregateInfo(tokenAddress, signal);
  const raw = unwrapData(res);
  return mapAggregateToResult(raw);
}

/* ----- Token States with Notes API (GET /v1/token-states/with-notes) ----- */

export interface TokenStateWithNotes {
  id: string;
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  imageUrl?: string | null;
  memo?: string;
  category?: string;
  status?: string;
  note?: { contents?: string };
  evaluation?: unknown;
  monitoringResults?: unknown[];
  [key: string]: unknown;
}

export interface TokenStatesWithNotesResponse {
  data: TokenStateWithNotes[];
  meta: { count: number; limit: number };
}

export async function fetchTokenStatesWithNotes(
  limit: number = 10
): Promise<TokenStatesWithNotesResponse> {
  const url = `${API_BASE_URL}/v1/token-states/with-notes?limit=${limit}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Token states failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}
